"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.quickOfferTool = void 0;
const zod_1 = require("zod");
exports.quickOfferTool = {
    name: 'quickOffer',
    description: `This tool detects and collects quick offer form fields from user messages. Call it when user provides information relevant to a quick offer inquiry or requests an offer.

    Available form fields and options are provided in the system message as:
    <tool-data toolName="quickOffer">
      <tool-data-entry key="fields">
        <tool-data-object>
          <tool-data-value key="name">field_name</tool-data-value><tool-data-value key="required">true</tool-data-value>
        </tool-data-object>
        <tool-data-object>
          <tool-data-value key="name">another_field</tool-data-value><tool-data-value key="required">false</tool-data-value>
        </tool-data-object>
      </tool-data-entry>
      <tool-data-entry key="options">
        <tool-data-object>
          <tool-data-value key="id">option_id</tool-data-value><tool-data-value key="name">Option Label</tool-data-value>
        </tool-data-object>
      </tool-data-entry>
    </tool-data>
    
    - "fields" entries define form field names and whether they are required.
    - "options" entries define selectable option IDs and their display names.
    
    ⚠️ CRITICAL — NEVER send the form without EXPLICIT user confirmation:
    - NEVER set sendForm to true unless the user has just explicitly said something like "send it", "submit", "yes, send", "go ahead" in their LATEST message.
    - NEVER infer consent from silence, from all fields being filled, or from any implicit action.
    - NEVER set sendForm to true proactively — it MUST always be a direct response to an explicit user instruction.
    - When all required fields and options are collected, ALWAYS ask: "I have all the details. Would you like me to submit the offer?" — then wait for their explicit confirmation.

    IMPORTANT: Call this tool whenever the user expresses intent to get an offer OR provides form field data.
    You MUST always pass ALL fields from the tool-data "fields" entry in the "fields" array — even when a field has no value yet (set value to null).
    You MUST always pass ALL options from the tool-data "options" entry in the "availableOptions" array.
    Set "selected: true" on options the user has chosen. After calling this tool, continue with your normal conversational response.

    You must always demand an option from <tool-data-entry key="options"> (do not mistake for any other data from other prompt instructions) before you can send the form, even if all required fields are filled. This is because options may contain important information about the user's preferences that is not captured in the form fields.

    Do not mix this tools with contact request - use this tool specifically for offer requests only.

    Examples:
    - User says "I need an offer" → fields: [{ name: "email", value: null, required: true }, ...], availableOptions: [...], sendForm: false
    - User provides email "jan@example.com" → fields: [{ name: "email", value: "jan@example.com", required: true }, ...]
    - User explicitly says "send", "submit", "yes send it" etc. → call with same fields + sendForm: true
    - Otherwise → ask the user if they want to submit once all required fields are filled

    This tool runs in background - always provide a helpful response to the user's message.
    `,
    zodSchema: zod_1.z.object({
        fields: zod_1.z.array(zod_1.z.object({
            name: zod_1.z.string().describe("Field name from tool-data fields entry"),
            value: zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.null()]).optional().describe("Extracted value from conversation, or null if not yet provided by the user"),
            required: zod_1.z.boolean().optional().describe("Whether this field is required, as defined in tool-data")
        })).describe("ALL fields from tool-data fields entry, each with extracted value (or null if not yet provided). Never omit a field."),
        availableOptions: zod_1.z.array(zod_1.z.object({
            id: zod_1.z.string().describe("Option ID from tool-data options entry"),
            name: zod_1.z.string().describe("Option display name from tool-data options entry"),
            selected: zod_1.z.boolean().describe("True if the user has chosen this option")
        })).optional().describe("ALL options from tool-data options entry with selection state"),
        sendForm: zod_1.z.boolean().optional().default(false).describe("NEVER set to true unless the user EXPLICITLY said to send/submit in their current message. Default is always false. All fields being filled alone is NOT sufficient — explicit verbal confirmation is required.")
    }),
    blockStream: false
};
